import { FC, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Clock, Edit } from 'lucide-react';
import { MainLayout } from '../MainLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from 'src/trpc';
import { PricingValidationSummary } from '../../components/pricing/PricingValidationSummary';

// Scope editing interfaces
interface RuleScope {
  customerClassification?: string;
  zipCodes?: string[];
  customerIds?: string[];
}

interface EditableRule {
  id: string;
  name: string;
  scope: RuleScope;
  [key: string]: unknown;
}

// Scope Editor Component
interface ScopeEditorProps {
  rule: EditableRule;
  onSave: (updatedRule: EditableRule) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

function ScopeEditor({ rule, onSave, isOpen, onOpenChange }: ScopeEditorProps) {
  const [scopeType, setScopeType] = useState<string>(() => {
    if (rule.scope.customerClassification) return 'classification';
    if (rule.scope.customerIds && rule.scope.customerIds.length > 0) return 'customers';
    return 'global';
  });

  const [customerClassification, setCustomerClassification] = useState(
    rule.scope.customerClassification || '',
  );

  const [zipCodes, setZipCodes] = useState<string[]>(rule.scope.zipCodes || []);
  const [zipCodeInput, setZipCodeInput] = useState('');

  const handleSave = () => {
    const newScope: RuleScope = {};

    // Add scope type criteria
    if (scopeType === 'classification' && customerClassification) {
      newScope.customerClassification = customerClassification;
    } else if (scopeType === 'customers' && rule.scope.customerIds) {
      newScope.customerIds = rule.scope.customerIds; // Keep existing for now
    }

    // Always add zip codes if selected
    if (zipCodes.length > 0) {
      newScope.zipCodes = zipCodes;
    }

    const updatedRule = {
      ...rule,
      scope: newScope,
    };

    onSave(updatedRule);
    onOpenChange(false);
  };

  const addZipCode = () => {
    const trimmedInput = zipCodeInput.trim();

    if (trimmedInput && !zipCodes.includes(trimmedInput)) {
      const newZipCodes = [...zipCodes, trimmedInput];
      setZipCodes(newZipCodes);
      setZipCodeInput('');
    }
  };

  const removeZipCode = (zipCode: string) => {
    setZipCodes(zipCodes.filter((z) => z !== zipCode));
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Edit Rule Scope: {rule.name}</SheetTitle>
        </SheetHeader>

        <div className="space-y-6 py-6">
          <div>
            <Label className="text-base font-medium">Customer Scope</Label>
            <p className="text-sm text-gray-600 mb-3">
              Choose which customers this rule applies to
            </p>
            <Select value={scopeType} onValueChange={setScopeType}>
              <SelectTrigger>
                <SelectValue placeholder="Select customer scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="global">All Customers</SelectItem>
                <SelectItem value="classification">Customer Type</SelectItem>
                <SelectItem value="customers">Specific Customers</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {scopeType === 'classification' && (
            <div>
              <Label className="text-base font-medium">Customer Type</Label>
              <Select value={customerClassification} onValueChange={setCustomerClassification}>
                <SelectTrigger className="mt-2">
                  <SelectValue placeholder="Select customer type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {scopeType === 'customers' && (
            <div>
              <Label className="text-base font-medium">Specific Customers</Label>
              <p className="text-sm text-gray-500 mt-1">
                Specific customer selection is not editable in this demo
              </p>
              <div className="mt-2 p-3 bg-gray-50 rounded border text-sm">
                {rule.scope.customerIds?.length || 0} customers selected
              </div>
            </div>
          )}

          <div>
            <Label className="text-base font-medium">Geographic Scope</Label>
            <p className="text-sm text-gray-600 mb-3">
              Restrict this rule to specific zip codes (optional)
            </p>

            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Type zip code and press Enter (e.g., 33496)"
                  value={zipCodeInput}
                  onChange={(e) => setZipCodeInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addZipCode();
                    }
                  }}
                  onBlur={addZipCode}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <Button onClick={addZipCode} variant="outline" size="sm" className="px-4">
                  Add
                </Button>
              </div>

              <div className="text-xs text-gray-500 flex items-center gap-1">
                💡 Tip: Press Enter or click outside the field to add zip codes automatically
              </div>
            </div>

            {zipCodes.length > 0 && (
              <div className="mt-4 space-y-2">
                <p className="text-sm font-medium text-green-700">
                  ✅ {zipCodes.length} zip code{zipCodes.length !== 1 ? 's' : ''} selected:
                </p>
                <div className="flex flex-wrap gap-2">
                  {zipCodes.map((zip) => (
                    <Badge key={zip} variant="secondary" className="text-sm py-1 px-2">
                      {zip}
                      <button
                        onClick={() => removeZipCode(zip)}
                        className="ml-2 text-red-500 hover:text-red-700 font-bold"
                        title="Remove zip code"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {zipCodes.length === 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded border text-sm text-gray-600">
                No zip code restrictions - rule applies to all locations
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-6 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Apply Changes & Re-test</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface PricingTestResult {
  appliedRules: Array<{
    ruleId: string;
    ruleName: string;
    configuration: {
      type: string;
      data: unknown;
      appliedValue: unknown;
    };
    scope: unknown;
    precedence: number;
    canCombine: boolean;
    appliedReason: string;
  }>;
  pricing: {
    containers: Array<{
      containerId: string;
      quantity: number;
      totalDays: number;
      basePriceInCents: number;
      adjustedPriceInCents: number;
      pricingModel: string;
      appliedRuleId?: string;
    }>;
    materials: {
      totalPriceInCents: number;
      weightInTons: number;
      effectiveWeightInTons: number;
      perTonRateInCents: number;
      minimumChargeInCents: number;
      pricingModel: string;
      appliedRuleId?: string;
      breakdown: Array<{
        materialId: string;
        quantity: number;
        priceInCents: number;
      }>;
    };
    addOns: Array<{
      addOnId: string;
      basePriceInCents: number;
      adjustedPriceInCents: number;
      appliedRuleId?: string;
    }>;
    discounts: Array<{
      type: string;
      percentage: number;
      amountInCents: number;
      appliedRuleId: string;
    }>;
    totals: {
      subtotalInCents: number;
      totalDiscountInCents: number;
      finalTotalInCents: number;
      breakdown: {
        containers: number;
        materials: number;
        addOns: number;
        cityFee: number;
        distanceSurcharge: number;
        discounts: number;
      };
    };
  };
  metadata: {
    evaluationDate: string;
    ruleResolutionLog: Array<{
      step: string;
      message: string;
      ruleIds: string[];
      details: unknown;
    }>;
    warnings: Array<{
      code: string;
      message: string;
      severity: string;
      context?: unknown;
    }>;
  };
  context: {
    order: {
      id: string;
      friendlyId: string;
      status: string;
      userId: string;
    };
    customer: {
      id: string;
      name: string;
      type: string;
      company?: string;
    };
    orderData: {
      totalDays: number;
      calculatedWeightInTons: number;
      containersCount: number;
      materialsCount: number;
      addOnsCount: number;
    };
    evaluationParameters: {
      evaluationDate: string;
      rulesProvided: number;
      organizationId?: string;
    };
  };
  debugData?: {
    fullOrder: unknown;
    fullCart: unknown;
    fullCustomer: unknown;
    fullAddress: unknown;
    containers: unknown[];
    materials: unknown[];
    addOns: unknown[];
    testedRules: unknown[];
    fullPricingInput: unknown;
    organization: unknown;
  };
}

export const PricingTestResults: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [testResult, setTestResult] = useState<PricingTestResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [parsedRules, setParsedRules] = useState<any[]>([]);

  // Scope editor state
  const [isScopeEditorOpen, setIsScopeEditorOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<EditableRule | null>(null);

  const orderId = searchParams.get('orderId');
  const rulesData = searchParams.get('rules');

  // Parse rules from URL parameters
  useEffect(() => {
    if (orderId && rulesData) {
      try {
        const rules = JSON.parse(decodeURIComponent(rulesData));

        // Ensure configurations have proper data structure (config.data) for backend compatibility
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const normalizedRules = rules.map((rule: any) => ({
          ...rule,
          // Ensure single configuration format
          configuration: rule.configuration || {
            type: 'UNKNOWN',
            data: {},
          },
        }));

        setParsedRules(normalizedRules);
        setError(null);
      } catch (error) {
        setError('Failed to parse pricing rules from URL parameters');
        setIsLoading(false);
      }
    } else {
      setError('Missing order ID or pricing rules in URL parameters');
      setIsLoading(false);
    }
  }, [orderId, rulesData]);

  const testPricingQuery = trpc.admin.pricingRule.testPricingRules.useQuery(
    {
      orderId: orderId || '',
      pricingRules: parsedRules,
    },
    {
      enabled: orderId !== null && parsedRules.length > 0,
    },
  );

  // Handle query success/error with useEffect (TanStack Query v5)
  useEffect(() => {
    if (testPricingQuery.data) {
      setTestResult(testPricingQuery.data as PricingTestResult);
      setIsLoading(false);
      setError(null);
    }
  }, [testPricingQuery.data]);

  useEffect(() => {
    if (testPricingQuery.error) {
      setError(testPricingQuery.error.message || 'Failed to test pricing rules');
      setIsLoading(false);
    }
  }, [testPricingQuery.error]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Scope editing functions
  const handleEditScope = () => {
    if (parsedRules.length > 0) {
      const rule = parsedRules[0]; // Edit the first rule for simplicity

      const editableRule = {
        id: rule.id || rule._id || 'rule-1',
        name: rule.name || 'Unnamed Rule',
        scope: rule.scope || {},
        ...rule,
      };

      setEditingRule(editableRule);
      setIsScopeEditorOpen(true);
    }
  };

  const handleSaveScope = (updatedRule: EditableRule) => {
    // Update the parsed rules with the new scope
    const updatedRules = parsedRules.map((rule) => {
      const ruleId = rule.id || rule._id;

      if (ruleId === updatedRule.id) {
        return { ...rule, scope: updatedRule.scope };
      }
      return rule;
    });

    // Update URL with the modified rules - try single encoding first
    const rulesJson = JSON.stringify(updatedRules);
    const encodedRules = encodeURIComponent(encodeURIComponent(rulesJson));
    const newUrl = `/pricing/test-results?orderId=${orderId}&rules=${encodedRules}`;

    // Try using window.location instead of navigate to force reload
    window.location.href = newUrl;
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex flex-row justify-between mb-12">
          <h1 className="flex-1 text-2xl font-bold">Pricing Test Results</h1>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p>Loading pricing test results...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="flex flex-row justify-between mb-12">
          <h1 className="flex-1 text-2xl font-bold">Pricing Test Results</h1>
        </div>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Error Loading Test Results</h2>
          <p className="text-slate-500 mb-4">{error}</p>
          <div className="space-y-2">
            <Button onClick={() => navigate('/pricing')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Pricing Rules
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setError(null);
                setIsLoading(true);
                if (orderId && rulesData && parsedRules.length > 0) {
                  testPricingQuery.refetch();
                }
              }}
            >
              Try Again
            </Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!testResult) {
    return (
      <MainLayout>
        <div className="flex flex-row justify-between mb-12">
          <h1 className="flex-1 text-2xl font-bold">Pricing Test Results</h1>
        </div>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">No Test Results Found</h2>
          <p className="text-slate-500 mb-4">Unable to load pricing test results.</p>
          <Button onClick={() => navigate('/pricing')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Pricing Rules
          </Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="flex items-center space-x-4 mb-4">
        <Button variant="outline" onClick={() => navigate('/pricing')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Pricing Rules
        </Button>
      </div>
      {/* Header */}
      <div className="flex flex-row justify-between mb-12">
        <div>
          <h1 className="flex-1 text-2xl font-bold">Pricing Test Results</h1>
          <p className="text-slate-500 mt-1 dark:text-slate-400">
            Order #{testResult.context.order.friendlyId}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={handleEditScope}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Scope
          </Button>
          <Badge variant={testResult.appliedRules.length > 0 ? 'default' : 'secondary'}>
            {testResult.appliedRules.length} Rules Applied
          </Badge>
        </div>
      </div>

      {/* Pricing Validation Summary - Compact view for pricing experts */}
      <PricingValidationSummary
        testResult={testResult}
        testedRules={testResult.debugData?.testedRules || parsedRules}
      />

      {/* Warnings and Metadata */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Warnings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Warnings & Notes ({testResult.metadata.warnings.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {testResult.metadata.warnings.length === 0 ? (
              <p className="text-gray-500">No warnings generated.</p>
            ) : (
              <div className="space-y-3">
                {testResult.metadata.warnings.map((warning, index) => (
                  <Alert
                    key={index}
                    variant={warning.severity === 'error' ? 'destructive' : 'default'}
                  >
                    <AlertDescription>
                      <strong>{warning.code}:</strong> {warning.message}
                    </AlertDescription>
                  </Alert>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Evaluation Metadata */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Evaluation Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div>
                <div className="font-medium">Evaluation Date</div>
                <div className="text-gray-600">
                  {formatDate(testResult.metadata.evaluationDate)}
                </div>
              </div>
              <div>
                <div className="font-medium">Rules Provided</div>
                <div className="text-gray-600">
                  {testResult.context.evaluationParameters.rulesProvided}
                </div>
              </div>
              <div>
                <div className="font-medium">Resolution Log</div>
                <div className="space-y-1">
                  {testResult.metadata.ruleResolutionLog.map((log, index) => (
                    <div key={index} className="text-gray-600 text-xs">
                      {log.step}: {log.message}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Debug Data Section */}
      {testResult.debugData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">🐛 Debug Information</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(testResult.debugData, null, 2));
                }}
              >
                Copy Debug Data
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  This section contains complete order, cart, rules, and evaluation data for
                  debugging. Use the "Copy Debug Data" button to copy the JSON for analysis.
                </AlertDescription>
              </Alert>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                  <div>
                    <strong>Order ID:</strong>{' '}
                    {
                      (testResult.debugData.fullOrder as { friendlyId: string } | undefined)
                        ?.friendlyId
                    }
                  </div>
                  <div>
                    <strong>Containers:</strong> {testResult.debugData.containers?.length || 0}
                  </div>
                  <div>
                    <strong>Materials:</strong> {testResult.debugData.materials?.length || 0}
                  </div>
                  <div>
                    <strong>Add-ons:</strong> {testResult.debugData.addOns?.length || 0}
                  </div>
                  <div>
                    <strong>Rules Tested:</strong> {testResult.debugData.testedRules?.length || 0}
                  </div>
                  <div>
                    <strong>Organization:</strong>{' '}
                    {(testResult.debugData.organization as { name: string } | undefined)?.name}
                  </div>
                </div>
              </div>

              <details className="bg-gray-50 p-4 rounded-lg">
                <summary className="cursor-pointer font-medium">
                  View Raw Debug Data (Click to expand)
                </summary>
                <pre className="mt-4 text-xs overflow-x-auto bg-white p-4 rounded border max-h-96">
                  {JSON.stringify(testResult.debugData, null, 2)}
                </pre>
              </details>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Back to Pricing Rules Button */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={() => navigate('/pricing')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Pricing Rules
        </Button>
      </div>

      {/* Scope Editor Drawer */}
      {editingRule && (
        <ScopeEditor
          rule={editingRule}
          onSave={handleSaveScope}
          isOpen={isScopeEditorOpen}
          onOpenChange={setIsScopeEditorOpen}
        />
      )}
    </MainLayout>
  );
};
